import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, categoryArticlesMap } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'baicai-jia';
  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];
  const articleIds = categoryArticlesMap[currentSlug] || [];
  const categoryArticles = articleIds.map((id) => articles.find((a) => a.id === id)).filter(Boolean);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${currentCategory.name} - 白菜网站`,
        description: `${currentCategory.description}，白菜网站每日更新最新${currentCategory.name}信息，帮你找到最实惠的购物机会。`,
        url: `https://www.baicaiwang.com/category/${currentSlug}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.baicaiwang.com/' },
            { '@type': 'ListItem', position: 2, name: currentCategory.name },
          ],
        },
      },
      {
        '@type': 'ItemList',
        itemListElement: categoryArticles.map((a, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `https://www.baicaiwang.com/article/${a.slug}`,
          name: a.title,
        })),
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${currentCategory.name} - 白菜网站最新2024`}
        description={`${currentCategory.description}，白菜网站每日更新最新${currentCategory.name}信息，汇聚全网白菜优惠活动，帮你找到最实惠的购物机会。`}
        keywords={`${currentCategory.name},白菜网站最新,最新白菜网站,白菜优惠活动`}
        canonicalUrl={`https://www.baicaiwang.com/category/${currentSlug}`}
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main>
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="font-heading text-base text-foreground-950 mb-4">全部分类</h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        currentSlug === cat.slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </span>
                <div>
                  <h1 className="font-heading text-xl md:text-2xl text-foreground-950">{currentCategory.name}</h1>
                  <p className="text-sm text-foreground-500 mt-0.5">{currentCategory.description}</p>
                </div>
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 text-foreground-500">
                  <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </span>
                  <p className="text-sm">暂无相关内容，请查看其他分类</p>
                  <Link to="/" className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}